import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, take } from 'rxjs';

import { ProcessRequestInviteResponse, SendInvitesResponse } from '@efficiently/models';

import { InviteRepository } from '@efficiently/client-api-invite';
import { GraphQLClient } from '@efficiently/client-core-graphql';

import {
	CANCEL_INVITE,
	PROCESS_REQUEST_INVITE,
	REQUEST_INVITE_TO_PROJECT,
	RESEND_ORG_INVITE,
	SEND_ORG_INVITES,
} from '../queries/invite.queries';

@Injectable()
export class InviteGqlRepository extends InviteRepository {
	private readonly apollo = inject(Apollo);

	public override sendOrgInvites(
		emails: string[],
		accountId: string,
		locationId: string,
		roleId?: string,
	): Observable<SendInvitesResponse> {
		return this.apollo
			.mutate({
				mutation: SEND_ORG_INVITES,
				variables: { emails, accountId, locationId, roleId },
				context: {
					client: GraphQLClient.Identity,
				},
			})
			.pipe(
				map(({ data }) => data?.sendOrgInvites),
				take(1),
			);
	}

	public override resendOrgInvite(contactId: string): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: RESEND_ORG_INVITE,
				variables: { contactId },
				context: {
					client: GraphQLClient.Identity,
				},
			})
			.pipe(
				map(({ data }) => data?.resendOrgInvite),
				take(1),
			);
	}

	public override cancelInvite(contactId: string): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: CANCEL_INVITE,
				variables: { contactId },
				context: {
					client: GraphQLClient.Identity,
				},
			})
			.pipe(
				map(({ data }) => data?.cancelContactInvite),
				take(1),
			);
	}

	public override requestInviteToProject(projectId: string, email: string, accountId: string): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: REQUEST_INVITE_TO_PROJECT,
				variables: {
					projectId,
					email,
					accountId,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				map(({ data }) => data?.requestInviteToProject),
				take(1),
			);
	}

	public override processRequestInvite(
		requestId: string,
		isDecline: boolean,
	): Observable<ProcessRequestInviteResponse> {
		return this.apollo
			.mutate({
				mutation: PROCESS_REQUEST_INVITE,
				variables: {
					requestId,
					isDecline,
				},
				context: {
					client: GraphQLClient.Core,
				},
			})
			.pipe(
				take(1),
				// TEMP-CODEGEN-FRONTIER(EFF-24658): result is the app-wide `ProcessRequestInviteResponse` domain shape.
				map(({ data }) => data.processRequestInvite as unknown as ProcessRequestInviteResponse),
			);
	}
}
